/**
 * Session analytics report opened from Trainers Report → VIEW.
 * Same chrome as the session report listing: filters, search, export, sortable table.
 * Report By: Subtopic (default) | Student
 */

import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PiSortAscendingLight, PiSortDescendingLight } from "react-icons/pi";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { MdFileDownload } from "react-icons/md";
import { Spinner } from "react-bootstrap";
import * as XLSX from "xlsx";
import {
  fetchSessionAnalyticsReport,
  type AnalyticsStudent,
  type AnalyticsSubtopic,
  type AnalyticsSession,
  type AttendanceLog,
  type GraspFeedbackRow,
} from "./sessionAnalyticsReportApi";
import GraspFeedbacksDialog from "./GraspFeedbacksDialog";
import StudentQuizDetailsDialog from "./StudentQuizDetailsDialog";
import type { QuizSlot } from "../../../livekit/liveQuizTypes";

type TabId = "subtopic" | "student";

function formatLogTime(raw: string | null | undefined): string {
  if (!raw) return "--";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleString();
}

function AttendanceLogsHover({
  studentName,
  label,
  logs,
}: {
  studentName: string;
  label: string;
  logs?: AttendanceLog[];
}) {
  const entries = (logs || []).filter((log) => {
    if (!log) return false;
    if (log.session_start_time || log.session_end_time) return true;
    return log.duration_minutes != null && Number(log.duration_minutes) > 0;
  });
  if (!label || label === "Absent" || label === "—") {
    return <span>{label || "—"}</span>;
  }
  if (!entries.length) {
    return <span>{label}</span>;
  }
  return (
    <Tooltip
      arrow
      placement="top"
      enterDelay={200}
      leaveDelay={200}
      describeChild
      disableInteractive={false}
      slotProps={{
        popper: { sx: { zIndex: 3000 } },
        tooltip: {
          sx: {
            bgcolor: "#f1f1f1",
            color: "#333",
            maxWidth: 440,
            maxHeight: 280,
            overflow: "hidden",
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            p: 0,
            "& .MuiTooltip-arrow": {
              color: "#f1f1f1",
              "&::before": { border: "1px solid #e0e0e0" },
            },
          },
        },
      }}
      title={
        <Box
          sx={{
            p: 1.25,
            bgcolor: "#f1f1f1",
            borderRadius: "5px",
            color: "#333",
            maxHeight: 280,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Typography
            component="h4"
            sx={{ m: 0, mb: 1, fontSize: 13, fontWeight: 700, color: "#111", flexShrink: 0 }}
          >
            Logs for {studentName}
          </Typography>
          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 2.25,
              overflowY: "auto",
              maxHeight: 230,
              pr: 0.5,
            }}
          >
            {entries.map((logEntry, index) => {
              const mins =
                logEntry.duration_minutes != null &&
                Number.isFinite(Number(logEntry.duration_minutes))
                  ? Math.max(0, Math.round(Number(logEntry.duration_minutes)))
                  : null;
              return (
                <Box component="li" key={index} sx={{ mb: 0.75, fontSize: 12 }}>
                  <strong>Login:</strong> {formatLogTime(logEntry.session_start_time)}
                  <br />
                  <strong>Logout:</strong> {formatLogTime(logEntry.session_end_time)}
                  <br />
                  <strong>Duration:</strong>{" "}
                  {mins != null ? `${mins} min` : "--"}
                </Box>
              );
            })}
          </Box>
        </Box>
      }
    >
      <Box
        component="span"
        sx={{
          display: "inline-block",
          cursor: "pointer",
          color: "#15803d",
          textDecoration: "underline",
          textUnderlineOffset: "2px",
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );
}

const selectStyle: React.CSSProperties = {
  height: 36,
  fontSize: 14,
  fontFamily: "inherit",
};

const headerCellStyle: React.CSSProperties = {
  backgroundColor: "#f5f5f5",
  borderRight: "1px solid #e0e0e0",
  fontWeight: 600,
  fontSize: 12,
  padding: "8px 12px",
  color: "#666",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 10,
};

const groupHeaderStyle: React.CSSProperties = {
  ...headerCellStyle,
  textAlign: "center",
};

const subHeaderStyle: React.CSSProperties = {
  ...headerCellStyle,
  top: 37,
  fontSize: 11,
  fontWeight: 600,
};

const bodyCellStyle: React.CSSProperties = {
  padding: "6px 12px",
  borderRight: "1px solid #e0e0e0",
  fontSize: 13,
  whiteSpace: "nowrap",
};

const commentCellStyle: React.CSSProperties = {
  ...bodyCellStyle,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  verticalAlign: "top",
  minWidth: 180,
  maxWidth: 280,
  width: 240,
};

function dash(value: number | string | null | undefined): string {
  if (value == null || value === "") return "—";
  return String(value);
}

function hasPctValue(value: number | null | undefined): boolean {
  return value != null && !Number.isNaN(Number(value));
}

function hasQuizScore(value: number | null | undefined): boolean {
  return value != null && !Number.isNaN(Number(value));
}

function pct(value: number | null | undefined): string {
  if (!hasPctValue(value)) return "—";
  return `${Math.round(Number(value))}%`;
}

function graspCellStyle(
  value: number | null | undefined,
  clickable: React.CSSProperties,
  plain: React.CSSProperties,
): React.CSSProperties {
  return hasPctValue(value) ? clickable : plain;
}

function mins(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const totalSec = Math.max(0, Math.round(Number(value) * 60));
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function num(value: number | null | undefined): number {
  if (value == null || Number.isNaN(Number(value))) return -1;
  return Number(value);
}

const SessionAnalyticsReport: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionId =
    searchParams.get("sessionId") ||
    String((location.state as { sessionId?: string } | null)?.sessionId || "");

  const [tab, setTab] = useState<TabId>("subtopic");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [session, setSession] = useState<AnalyticsSession | null>(null);
  const [subtopics, setSubtopics] = useState<AnalyticsSubtopic[]>([]);
  const [students, setStudents] = useState<AnalyticsStudent[]>([]);
  const [attitudeOptions, setAttitudeOptions] = useState<string[]>([]);
  const [graspFeedbacks, setGraspFeedbacks] = useState<GraspFeedbackRow[]>([]);
  const [graspFeedbacksOpen, setGraspFeedbacksOpen] = useState(false);
  const [graspPopupFilter, setGraspPopupFilter] = useState<
    "partial_did_not" | "partial" | "not_understood" | "understood"
  >("partial_did_not");
  const [graspPopupTopicId, setGraspPopupTopicId] = useState<string | null>(null);
  const [graspPopupTopicTitle, setGraspPopupTopicTitle] = useState<string | null>(null);
  const [graspPopupStudentId, setGraspPopupStudentId] = useState<string | null>(null);

  const [quizDetailsOpen, setQuizDetailsOpen] = useState(false);
  const [quizDetailsSlot, setQuizDetailsSlot] = useState<QuizSlot>("pre");
  const [quizDetailsStudentId, setQuizDetailsStudentId] = useState("");
  const [quizDetailsStudentName, setQuizDetailsStudentName] = useState("");
  const [quizDetailsTopicId, setQuizDetailsTopicId] = useState<string | null>(null);
  const [quizDetailsTopicTitle, setQuizDetailsTopicTitle] = useState<string | null>(null);

  const [selectedSubtopic, setSelectedSubtopic] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedAttitude, setSelectedAttitude] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "ascending" | "descending" | "";
  }>({ key: "", direction: "" });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedIds(new Set());
  }, [selectedSubtopic, selectedBranch, selectedAttitude, searchQuery]);

  useEffect(() => {
    if (!sessionId) {
      setError("Missing session id.");
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      setSession(null);
      setSubtopics([]);
      setStudents([]);
      setAttitudeOptions([]);
      setGraspFeedbacks([]);
      setSelectedSubtopic("");
      setSelectedIds(new Set());
      try {
        const data = await fetchSessionAnalyticsReport(sessionId);
        if (cancelled) return;
        setSession(data.session);
        setSubtopics(data.subtopics);
        setStudents(data.students);
        setAttitudeOptions(data.attitude_options);
        setGraspFeedbacks(data.grasp_feedbacks || []);
      } catch (err) {
        console.error("session analytics load failed", err);
        if (!cancelled) setError("Could not load session report.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const branchOptions = useMemo(() => {
    const set = new Set<string>();
    if (session?.branch) set.add(session.branch);
    for (const row of students) {
      if (row.branch) set.add(row.branch);
    }
    return Array.from(set).sort();
  }, [session, students]);

  const navTitle =
    session?.session_name ||
    (location.state as { sessionTitle?: string } | null)?.sessionTitle ||
    "";

  const filteredSubtopics = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return subtopics;
    return subtopics.filter((row) => row.title.toLowerCase().includes(q));
  }, [subtopics, searchQuery]);

  const filteredStudents = useMemo(() => {
    return students.filter((row) => {
      if (selectedBranch && String(row.branch || "") !== selectedBranch) return false;
      if (selectedAttitude && !(row.attitude || []).includes(selectedAttitude)) {
        return false;
      }
      const q = searchQuery.trim().toLowerCase();
      if (q) {
        const hay = [row.student_name, row.comment, ...(row.attitude || [])]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [students, selectedBranch, selectedAttitude, searchQuery]);

  const studentViewRows = useMemo(() => {
    return filteredStudents.map((row) => {
      if (!selectedSubtopic) return row;
      const quiz = row.quiz_by_subtopic?.[selectedSubtopic] || {};
      const grasp = row.grasp_by_subtopic?.[selectedSubtopic];
      return {
        ...row,
        pre_quiz_score: quiz.pre?.score ?? null,
        pre_quiz_max: quiz.pre?.max ?? null,
        post_quiz_score: quiz.post?.score ?? null,
        post_quiz_max: quiz.post?.max ?? null,
        grasp_understood_percent: grasp?.understood ?? null,
        grasp_partial_percent: grasp?.partial ?? null,
        grasp_did_not_percent: grasp?.did_not ?? null,
        distraction_score:
          row.distraction_by_subtopic?.[selectedSubtopic] ?? null,
      };
    });
  }, [filteredStudents, selectedSubtopic]);

  const sortedSubtopics = useMemo(() => {
    if (!sortConfig.key || tab !== "subtopic") return filteredSubtopics;
    const dir = sortConfig.direction === "ascending" ? 1 : -1;
    const key = sortConfig.key as keyof AnalyticsSubtopic;
    return [...filteredSubtopics].sort((a, b) => {
      if (key === "title") {
        return String(a.title || "").localeCompare(String(b.title || "")) * dir;
      }
      return (num(a[key] as number) - num(b[key] as number)) * dir;
    });
  }, [filteredSubtopics, sortConfig, tab]);

  const sortedStudents = useMemo(() => {
    if (!sortConfig.key || tab !== "student") return studentViewRows;
    const dir = sortConfig.direction === "ascending" ? 1 : -1;
    const key = sortConfig.key as keyof AnalyticsStudent;
    return [...studentViewRows].sort((a, b) => {
      if (key === "student_name" || key === "attendance_label") {
        return (
          String(a[key] || "").localeCompare(String(b[key] || "")) * dir
        );
      }
      return (num(a[key] as number) - num(b[key] as number)) * dir;
    });
  }, [studentViewRows, sortConfig, tab]);

  const preMax = useMemo(() => {
    const vals = sortedStudents
      .map((r) => r.pre_quiz_max)
      .filter((v): v is number => v != null && v > 0);
    return vals.length ? Math.max(...vals) : null;
  }, [sortedStudents]);

  const postMax = useMemo(() => {
    const vals = sortedStudents
      .map((r) => r.post_quiz_max)
      .filter((v): v is number => v != null && v > 0);
    return vals.length ? Math.max(...vals) : null;
  }, [sortedStudents]);

  const visibleIds =
    tab === "subtopic"
      ? sortedSubtopics.map((r) => r.id)
      : sortedStudents.map((r) => r.student_id);
  const visibleSelectedCount = visibleIds.filter((id) => selectedIds.has(id)).length;

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "ascending" };
      if (prev.direction === "ascending") return { key, direction: "descending" };
      return { key: "", direction: "" };
    });
  };

  const openGraspFeedbacks = (
    level: "partial_did_not" | "partial" | "not_understood" | "understood",
    opts?: {
      topicId?: string | null;
      topicTitle?: string | null;
      studentId?: string | null;
    },
  ) => {
    setGraspPopupFilter(level);
    setGraspPopupTopicId(opts?.topicId ?? null);
    setGraspPopupTopicTitle(opts?.topicTitle ?? null);
    setGraspPopupStudentId(opts?.studentId ?? null);
    setGraspFeedbacksOpen(true);
  };

  const graspPopupOptsForStudent = (studentId: string) => {
    if (!selectedSubtopic) return { studentId };
    const sub = subtopics.find((s) => s.id === selectedSubtopic);
    return {
      studentId,
      topicId: selectedSubtopic,
      topicTitle: sub?.title ?? null,
    };
  };

  const openQuizDetails = (
    slot: QuizSlot,
    row: AnalyticsStudent,
  ) => {
    const sub = selectedSubtopic
      ? subtopics.find((s) => s.id === selectedSubtopic)
      : null;
    setQuizDetailsSlot(slot);
    setQuizDetailsStudentId(row.student_id);
    setQuizDetailsStudentName(row.student_name || row.student_id);
    setQuizDetailsTopicId(selectedSubtopic || null);
    setQuizDetailsTopicTitle(sub?.title ?? null);
    setQuizDetailsOpen(true);
  };

  const quizClickCellStyle: React.CSSProperties = {
    ...bodyCellStyle,
    cursor: "pointer",
    color: "#2563eb",
    fontWeight: 600,
    textDecoration: "underline",
    textUnderlineOffset: 2,
  };

  const quizCellStyle = (
    value: number | null | undefined,
  ): React.CSSProperties =>
    hasQuizScore(value) ? quizClickCellStyle : bodyCellStyle;

  const graspClickCellStyle: React.CSSProperties = {
    ...bodyCellStyle,
    cursor: "pointer",
    color: "#0f172a",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  };

  const sortIcon = (key: string) => {
    if (sortConfig.key !== key) return <HiOutlineDotsVertical size={16} />;
    return sortConfig.direction === "ascending" ? (
      <PiSortAscendingLight size={16} />
    ) : (
      <PiSortDescendingLight size={16} />
    );
  };

  const handleSelectAll = () => {
    const allVisible =
      visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
    if (allVisible) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleIds));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExport = () => {
    if (tab === "subtopic") {
      const source =
        visibleSelectedCount > 0
          ? sortedSubtopics.filter((r) => selectedIds.has(r.id))
          : sortedSubtopics;
      const sheet = XLSX.utils.json_to_sheet(
        source.map((row, i) => ({
          Sn: i + 1,
          "Sub-Topic Name": row.title,
          "Planned (mins)": row.planned_minutes,
          "Wall-clock (mins)": row.actual_minutes,
          "Distraction %": row.distraction_percent,
          "Quiz Pre %": row.quiz_pre_percent,
          "Quiz Post %": row.quiz_post_percent,
          "Grasping Understood %": row.grasp_understood_percent,
          "Grasping Partial %": row.grasp_partial_percent,
          "Grasping Did Not %": row.grasp_did_not_percent,
          QA: row.qa_count,
        })),
      );
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, "By SubTopic");
      XLSX.writeFile(book, `${navTitle || "session"}-subtopic-report.xlsx`);
      return;
    }
    const source =
      visibleSelectedCount > 0
        ? sortedStudents.filter((r) => selectedIds.has(r.student_id))
        : sortedStudents;
    const sheet = XLSX.utils.json_to_sheet(
      source.map((row, i) => ({
        Sn: i + 1,
        "Student Name": row.student_name,
        Attendance: row.attendance_label,
        "Pre-Quiz Score": row.pre_quiz_score,
        "Post-Quiz Score": row.post_quiz_score,
        "Distraction Score": row.distraction_score,
        "Grasping Understood %": row.grasp_understood_percent,
        "Grasping Partial %": row.grasp_partial_percent,
        "Grasping Did Not %": row.grasp_did_not_percent,
        Attitude: (row.attitude || []).join(", "),
        Comment: row.comment,
        Ratings: row.rating_percent,
      })),
    );
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "By Student");
    XLSX.writeFile(book, `${navTitle || "session"}-student-report.xlsx`);
  };

  const sortableHeader = (
    label: string,
    key: string,
    extra?: React.CSSProperties,
    rowSpan?: number,
  ) => (
    <TableCell
      rowSpan={rowSpan}
      onClick={() => handleSort(key)}
      style={{ ...headerCellStyle, cursor: "pointer", ...extra }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <span>{label}</span>
        <span style={{ marginLeft: 4 }}>{sortIcon(key)}</span>
      </Box>
    </TableCell>
  );

  const exportCount =
    visibleSelectedCount > 0
      ? visibleSelectedCount
      : tab === "subtopic"
        ? sortedSubtopics.length
        : sortedStudents.length;

  return (
    <div className="container-fluid p-0 pe-2" style={{ marginTop: 12 }}>
      <Card>
        <CardContent
          style={{
            height: "calc(100vh - 90px)",
            overflow: "hidden",
            padding: 16,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            mb={1}
            style={{ flexShrink: 0 }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              gap="12px"
              flexWrap="wrap"
            >
              <Box display="flex" alignItems="center" gap="12px" flexWrap="wrap">
                <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
                  <InputLabel style={{ fontSize: 14 }}>Report By</InputLabel>
                  <Select
                    value={tab}
                    label="Report By"
                    onChange={(e) => {
                      setTab(e.target.value as TabId);
                      setSortConfig({ key: "", direction: "" });
                      setSelectedIds(new Set());
                    }}
                    style={selectStyle}
                  >
                    <MenuItem value="subtopic">Subtopic</MenuItem>
                    <MenuItem value="student">Student</MenuItem>
                  </Select>
                </FormControl>

                {tab === "student" ? (
                  <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
                    <InputLabel style={{ fontSize: 14 }}>SubTopic</InputLabel>
                    <Select
                      value={selectedSubtopic}
                      label="SubTopic"
                      onChange={(e) => setSelectedSubtopic(e.target.value)}
                      style={selectStyle}
                    >
                      <MenuItem value="">All</MenuItem>
                      {subtopics.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}

                <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                  <InputLabel style={{ fontSize: 14 }}>Track</InputLabel>
                  <Select
                    value={session?.track_name || ""}
                    label="Track"
                    style={selectStyle}
                  >
                    <MenuItem value={session?.track_name || ""}>
                      {session?.track_name || "All"}
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
                  <InputLabel style={{ fontSize: 14 }}>Course</InputLabel>
                  <Select
                    value={session?.course_name || ""}
                    label="Course"
                    style={selectStyle}
                  >
                    <MenuItem value={session?.course_name || ""}>
                      {session?.course_name || "All"}
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                  <InputLabel style={{ fontSize: 14 }}>Batch</InputLabel>
                  <Select
                    value={session?.batch_name || ""}
                    label="Batch"
                    style={selectStyle}
                  >
                    <MenuItem value={session?.batch_name || ""}>
                      {session?.batch_name || "All"}
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                  <InputLabel style={{ fontSize: 14 }}>Branch</InputLabel>
                  <Select
                    value={selectedBranch}
                    label="Branch"
                    onChange={(e) => {
                      setSelectedBranch(e.target.value);
                      setSelectedIds(new Set());
                    }}
                    style={selectStyle}
                  >
                    <MenuItem value="">All</MenuItem>
                    {branchOptions.map((b) => (
                      <MenuItem key={b} value={b}>
                        {b}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {tab === "subtopic" ? (
                  <TextField
                    size="small"
                    label="Session Name"
                    value={navTitle}
                    InputProps={{ readOnly: true }}
                    sx={{
                      minWidth: 180,
                      "& .MuiInputBase-root": { height: 36, fontSize: 14 },
                    }}
                  />
                ) : (
                  <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
                    <InputLabel style={{ fontSize: 14 }}>Attitude List</InputLabel>
                    <Select
                      value={selectedAttitude}
                      label="Attitude List"
                      onChange={(e) => {
                        setSelectedAttitude(e.target.value);
                        setSelectedIds(new Set());
                      }}
                      style={selectStyle}
                    >
                      <MenuItem value="">All</MenuItem>
                      {attitudeOptions.map((a) => (
                        <MenuItem key={a} value={a}>
                          {a}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>

              <Box display="flex" alignItems="center" gap="8px">
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    minWidth: 200,
                    height: 36,
                    fontSize: 14,
                    fontFamily: "inherit",
                    outline: "none",
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExport}
                  disabled={exportCount === 0}
                  startIcon={<MdFileDownload />}
                  style={{
                    height: 36,
                    fontSize: 12,
                    textTransform: "none",
                    border: "1px solid #ccc",
                    color: "#666",
                    whiteSpace: "nowrap",
                  }}
                >
                  Export{visibleSelectedCount > 0 ? ` (${visibleSelectedCount})` : ""}
                </Button>
              </Box>
            </Box>
          </Box>

          <TableContainer
            component={Paper}
            style={{ marginTop: 8, flexGrow: 1, overflow: "auto" }}
          >
            {error ? (
              <Box p={3}>
                <Typography color="error">{error}</Typography>
              </Box>
            ) : loading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </Box>
            ) : tab === "subtopic" ? (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      rowSpan={2}
                      style={{
                        ...headerCellStyle,
                        minWidth: 50,
                        textAlign: "center",
                      }}
                    >
                      <Checkbox
                        checked={
                          visibleIds.length > 0 &&
                          visibleSelectedCount === visibleIds.length
                        }
                        indeterminate={
                          visibleSelectedCount > 0 &&
                          visibleSelectedCount < visibleIds.length
                        }
                        onChange={handleSelectAll}
                        size="small"
                      />
                    </TableCell>
                    <TableCell rowSpan={2} style={{ ...headerCellStyle, minWidth: 48 }}>
                      Sn
                    </TableCell>
                    {sortableHeader("Sub-Topic Name", "title", { minWidth: 180 }, 2)}
                    <TableCell colSpan={2} style={groupHeaderStyle}>
                      Duration
                    </TableCell>
                    {sortableHeader("Distraction %", "distraction_percent", undefined, 2)}
                    <TableCell colSpan={2} style={groupHeaderStyle}>
                      Quiz Scores
                    </TableCell>
                    <TableCell colSpan={3} style={groupHeaderStyle}>
                      Grasping
                    </TableCell>
                    {sortableHeader("QA", "qa_count", undefined, 2)}
                  </TableRow>
                  <TableRow>
                    <TableCell
                      onClick={() => handleSort("planned_minutes")}
                      style={{ ...subHeaderStyle, cursor: "pointer" }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        Planned
                        <span style={{ marginLeft: 4 }}>{sortIcon("planned_minutes")}</span>
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("actual_minutes")}
                      style={{ ...subHeaderStyle, cursor: "pointer" }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        Wall-clock
                        <span style={{ marginLeft: 4 }}>{sortIcon("actual_minutes")}</span>
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("quiz_pre_percent")}
                      style={{ ...subHeaderStyle, cursor: "pointer" }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        Pre
                        <span style={{ marginLeft: 4 }}>{sortIcon("quiz_pre_percent")}</span>
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("quiz_post_percent")}
                      style={{ ...subHeaderStyle, cursor: "pointer" }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        Post
                        <span style={{ marginLeft: 4 }}>{sortIcon("quiz_post_percent")}</span>
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("grasp_understood_percent")}
                      style={{ ...subHeaderStyle, cursor: "pointer" }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        Understood
                        <span style={{ marginLeft: 4 }}>
                          {sortIcon("grasp_understood_percent")}
                        </span>
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("grasp_partial_percent")}
                      style={{ ...subHeaderStyle, cursor: "pointer" }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        Partial
                        <span style={{ marginLeft: 4 }}>
                          {sortIcon("grasp_partial_percent")}
                        </span>
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("grasp_did_not_percent")}
                      style={{ ...subHeaderStyle, cursor: "pointer" }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        Did Not
                        <span style={{ marginLeft: 4 }}>
                          {sortIcon("grasp_did_not_percent")}
                        </span>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedSubtopics.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={12}
                        align="center"
                        style={{ padding: 20, color: "#666" }}
                      >
                        No subtopics found for this session.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedSubtopics.map((row, index) => (
                      <TableRow
                        key={row.id || index}
                        style={{
                          backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f0f0f0",
                            cursor: "pointer",
                          },
                        }}
                      >
                        <TableCell style={{ ...bodyCellStyle, textAlign: "center" }}>
                          <Checkbox
                            checked={selectedIds.has(row.id)}
                            onChange={() => toggleSelect(row.id)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell style={bodyCellStyle}>{index + 1}</TableCell>
                        <TableCell style={{ ...bodyCellStyle, fontWeight: 600 }}>
                          {row.title}
                        </TableCell>
                        <TableCell style={bodyCellStyle}>
                          {mins(row.planned_minutes)}
                        </TableCell>
                        <TableCell style={bodyCellStyle}>
                          {mins(row.actual_minutes)}
                        </TableCell>
                        <TableCell style={bodyCellStyle}>
                          {pct(row.distraction_percent)}
                        </TableCell>
                        <TableCell style={bodyCellStyle}>
                          {pct(row.quiz_pre_percent)}
                        </TableCell>
                        <TableCell style={bodyCellStyle}>
                          {pct(row.quiz_post_percent)}
                        </TableCell>
                        <TableCell
                          style={graspCellStyle(
                            row.grasp_understood_percent,
                            graspClickCellStyle,
                            bodyCellStyle,
                          )}
                          title={
                            hasPctValue(row.grasp_understood_percent)
                              ? "View Understood feedbacks"
                              : undefined
                          }
                          onClick={
                            hasPctValue(row.grasp_understood_percent)
                              ? () =>
                                  openGraspFeedbacks("understood", {
                                    topicId: row.id,
                                    topicTitle: row.title,
                                  })
                              : undefined
                          }
                        >
                          {pct(row.grasp_understood_percent)}
                        </TableCell>
                        <TableCell
                          style={graspCellStyle(
                            row.grasp_partial_percent,
                            graspClickCellStyle,
                            bodyCellStyle,
                          )}
                          title={
                            hasPctValue(row.grasp_partial_percent)
                              ? "View Partial feedbacks"
                              : undefined
                          }
                          onClick={
                            hasPctValue(row.grasp_partial_percent)
                              ? () =>
                                  openGraspFeedbacks("partial", {
                                    topicId: row.id,
                                    topicTitle: row.title,
                                  })
                              : undefined
                          }
                        >
                          {pct(row.grasp_partial_percent)}
                        </TableCell>
                        <TableCell
                          style={graspCellStyle(
                            row.grasp_did_not_percent,
                            graspClickCellStyle,
                            bodyCellStyle,
                          )}
                          title={
                            hasPctValue(row.grasp_did_not_percent)
                              ? "View Did Not feedbacks"
                              : undefined
                          }
                          onClick={
                            hasPctValue(row.grasp_did_not_percent)
                              ? () =>
                                  openGraspFeedbacks("not_understood", {
                                    topicId: row.id,
                                    topicTitle: row.title,
                                  })
                              : undefined
                          }
                        >
                          {pct(row.grasp_did_not_percent)}
                        </TableCell>
                        <TableCell style={bodyCellStyle}>{dash(row.qa_count)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      rowSpan={2}
                      style={{
                        ...headerCellStyle,
                        minWidth: 50,
                        textAlign: "center",
                      }}
                    >
                      <Checkbox
                        checked={
                          visibleIds.length > 0 &&
                          visibleSelectedCount === visibleIds.length
                        }
                        indeterminate={
                          visibleSelectedCount > 0 &&
                          visibleSelectedCount < visibleIds.length
                        }
                        onChange={handleSelectAll}
                        size="small"
                      />
                    </TableCell>
                    <TableCell rowSpan={2} style={{ ...headerCellStyle, minWidth: 48 }}>
                      Sn
                    </TableCell>
                    {sortableHeader("Student Name", "student_name", { minWidth: 160 }, 2)}
                    {sortableHeader(
                      `Attendance (${session?.planned_minutes ? `${session.planned_minutes}mins` : "—"})`,
                      "attendance_minutes",
                      undefined,
                      2,
                    )}
                    {sortableHeader(
                      `Pre-Quiz Score${preMax ? ` (${preMax}-score)` : ""}`,
                      "pre_quiz_score",
                      undefined,
                      2,
                    )}
                    {sortableHeader(
                      `Post-Quiz Score${postMax ? ` (${postMax})` : ""}`,
                      "post_quiz_score",
                      undefined,
                      2,
                    )}
                    {sortableHeader("Distraction Score", "distraction_score", undefined, 2)}
                    <TableCell colSpan={3} style={groupHeaderStyle}>
                      Grasping
                    </TableCell>
                    {sortableHeader("Attitude", "comment", undefined, 2)}
                    <TableCell
                      rowSpan={2}
                      style={{
                        ...headerCellStyle,
                        minWidth: 180,
                        maxWidth: 280,
                        width: 240,
                      }}
                    >
                      Comment
                    </TableCell>
                    {sortableHeader("Ratings", "rating_percent", undefined, 2)}
                  </TableRow>
                  <TableRow>
                    <TableCell
                      onClick={() => handleSort("grasp_understood_percent")}
                      style={{ ...subHeaderStyle, cursor: "pointer" }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        Understood
                        <span style={{ marginLeft: 4 }}>
                          {sortIcon("grasp_understood_percent")}
                        </span>
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("grasp_partial_percent")}
                      style={{ ...subHeaderStyle, cursor: "pointer" }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        Partial
                        <span style={{ marginLeft: 4 }}>
                          {sortIcon("grasp_partial_percent")}
                        </span>
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("grasp_did_not_percent")}
                      style={{ ...subHeaderStyle, cursor: "pointer" }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        Did Not
                        <span style={{ marginLeft: 4 }}>
                          {sortIcon("grasp_did_not_percent")}
                        </span>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedStudents.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={13}
                        align="center"
                        style={{ padding: 20, color: "#666" }}
                      >
                        No students found for this session.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedStudents.map((row, index) => (
                      <TableRow
                        key={row.student_id || index}
                        style={{
                          backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f0f0f0",
                            cursor: "pointer",
                          },
                        }}
                      >
                        <TableCell style={{ ...bodyCellStyle, textAlign: "center" }}>
                          <Checkbox
                            checked={selectedIds.has(row.student_id)}
                            onChange={() => toggleSelect(row.student_id)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell style={bodyCellStyle}>{index + 1}</TableCell>
                        <TableCell style={{ ...bodyCellStyle, fontWeight: 600 }}>
                          {row.student_name}
                        </TableCell>
                        <TableCell style={bodyCellStyle}>
                          <AttendanceLogsHover
                            studentName={row.student_name}
                            label={row.attendance_label || "—"}
                            logs={row.attendance_logs}
                          />
                        </TableCell>
                        <TableCell
                          style={quizCellStyle(row.pre_quiz_score)}
                          title={
                            hasQuizScore(row.pre_quiz_score)
                              ? "View pre-quiz answers"
                              : undefined
                          }
                          onClick={
                            hasQuizScore(row.pre_quiz_score)
                              ? () => openQuizDetails("pre", row)
                              : undefined
                          }
                        >
                          {dash(row.pre_quiz_score)}
                        </TableCell>
                        <TableCell
                          style={quizCellStyle(row.post_quiz_score)}
                          title={
                            hasQuizScore(row.post_quiz_score)
                              ? "View post-quiz answers"
                              : undefined
                          }
                          onClick={
                            hasQuizScore(row.post_quiz_score)
                              ? () => openQuizDetails("post", row)
                              : undefined
                          }
                        >
                          {dash(row.post_quiz_score)}
                        </TableCell>
                        <TableCell style={bodyCellStyle}>
                          {dash(row.distraction_score)}
                        </TableCell>
                        <TableCell
                          style={graspCellStyle(
                            row.grasp_understood_percent,
                            graspClickCellStyle,
                            bodyCellStyle,
                          )}
                          title={
                            hasPctValue(row.grasp_understood_percent)
                              ? "View Understood feedbacks"
                              : undefined
                          }
                          onClick={
                            hasPctValue(row.grasp_understood_percent)
                              ? () =>
                                  openGraspFeedbacks(
                                    "understood",
                                    graspPopupOptsForStudent(row.student_id),
                                  )
                              : undefined
                          }
                        >
                          {pct(row.grasp_understood_percent)}
                        </TableCell>
                        <TableCell
                          style={graspCellStyle(
                            row.grasp_partial_percent,
                            graspClickCellStyle,
                            bodyCellStyle,
                          )}
                          title={
                            hasPctValue(row.grasp_partial_percent)
                              ? "View Partial feedbacks"
                              : undefined
                          }
                          onClick={
                            hasPctValue(row.grasp_partial_percent)
                              ? () =>
                                  openGraspFeedbacks(
                                    "partial",
                                    graspPopupOptsForStudent(row.student_id),
                                  )
                              : undefined
                          }
                        >
                          {pct(row.grasp_partial_percent)}
                        </TableCell>
                        <TableCell
                          style={graspCellStyle(
                            row.grasp_did_not_percent,
                            graspClickCellStyle,
                            bodyCellStyle,
                          )}
                          title={
                            hasPctValue(row.grasp_did_not_percent)
                              ? "View Did Not feedbacks"
                              : undefined
                          }
                          onClick={
                            hasPctValue(row.grasp_did_not_percent)
                              ? () =>
                                  openGraspFeedbacks(
                                    "not_understood",
                                    graspPopupOptsForStudent(row.student_id),
                                  )
                              : undefined
                          }
                        >
                          {pct(row.grasp_did_not_percent)}
                        </TableCell>
                        <TableCell style={bodyCellStyle}>
                          {(row.attitude || []).join(", ") || "—"}
                        </TableCell>
                        <TableCell style={commentCellStyle}>
                          {row.comment || "—"}
                        </TableCell>
                        <TableCell style={bodyCellStyle}>
                          {pct(row.rating_percent)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </CardContent>
      </Card>

      <GraspFeedbacksDialog
        open={graspFeedbacksOpen}
        onClose={() => setGraspFeedbacksOpen(false)}
        sessionId={sessionId}
        sessionName={session?.session_name}
        rows={graspFeedbacks}
        onRowsChange={setGraspFeedbacks}
        initialLevelFilter={graspPopupFilter}
        topicId={graspPopupTopicId}
        topicTitle={graspPopupTopicTitle}
        studentId={graspPopupStudentId}
      />

      <StudentQuizDetailsDialog
        open={quizDetailsOpen}
        onClose={() => setQuizDetailsOpen(false)}
        sessionId={sessionId}
        sessionName={session?.session_name}
        studentId={quizDetailsStudentId}
        studentName={quizDetailsStudentName}
        slot={quizDetailsSlot}
        topicId={quizDetailsTopicId}
        topicTitle={quizDetailsTopicTitle}
      />
    </div>
  );
};

export default SessionAnalyticsReport;
